import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../api";
import {
    ConnectData,
    NavigationParams,
    NpsData,
    NumberScaleData,
    OrderData,
    PlainDetails,
    SelectData,
    TextData
} from "../../types/admin";
import {Theme} from "../../types/games";

export type StatisticsData = {
    key: string;
    type: string;
    type_display: string;
    title: string;
    average_points: number;
    average_duration: string;
    max_points: number;
    connect_data: ConnectData;
    select_data: SelectData[];
    order_data: OrderData;
    text_data: TextData;
    number_scale_data: NumberScaleData[];
    nps_data: NpsData;
    average_rating: number | null;
    played: number;
}

export interface StatisticsState {
    loaded: boolean;
    error: string | null;
    statistics: StatisticsData[];
    name: string;
    average_duration: string;
    average_points: number;
    max_points: number;
    sections: number;
    questions: number;
    slides: number;
    pages: number;
    played: number;
    statistics_pdf_url: string;
    theme: Theme | null;
    team_options: PlainDetails[];
    selected_teams: PlainDetails[];
}

const initialState: StatisticsState = {
    loaded: false,
    error: null,
    name: '',
    statistics: [],
    average_duration: '0',
    average_points: 0,
    max_points: 0,
    sections: 0,
    questions: 0,
    slides: 0,
    pages: 0,
    played: 0,
    statistics_pdf_url: '',
    theme: null,
    team_options: [],
    selected_teams: []
};

type Params = {
    slug: string;
    teams?: string;
}

const extractParams = (params: Params): NavigationParams => {
    let _params: any = {};
    _params.slug = params.slug;
    if (params.teams) {
        _params.teams = params.teams;
    }
    return _params;
}

export const getStatistics = createAsyncThunk(
    'statistics/getStatistics',
    async (params: Params, thunkAPI) => {
        const _params = extractParams(params);
        const {slug} = params;
        try {
            const response =  await api.get(`/API/statistics/${slug}`, _params);
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

const slice = createSlice({
    name: 'statistics',
    initialState,
    reducers: {
        changeStatistics: (state, action: PayloadAction<Partial<StatisticsState>>) => {
            return {
                ...state,
                ...action.payload
            };
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getStatistics.pending, (state) => {
            state.loaded = false;
            state.error = null;
        });
        builder.addCase(getStatistics.fulfilled, (state, action) => {
            state.loaded = true;
            Object.assign(state, action.payload as StatisticsState);
        });
        builder.addCase(getStatistics.rejected, (state, action) => {
            state.loaded = true;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
    }
});

export default slice.reducer;
export const {changeStatistics} = slice.actions;